import { defineMessages, MessageDescriptor } from 'react-intl';
import type { RevisionGroupId } from '../assets/revisionCards';

/** The copy of the Overview tab (Releases v2, design b1–b4, e5–e7). */
export const overviewMessages = defineMessages({
  doneTitle: { id: 'app.sml.releases.overview.done.title', defaultMessage: 'Done' },
  doneSubtitle: { id: 'app.sml.releases.overview.done.subtitle', defaultMessage: 'What already went out' },
  doneSince: { id: 'app.sml.releases.overview.done.since', defaultMessage: 'since {date}' },
  postsPublished: { id: 'app.sml.releases.overview.done.posts', defaultMessage: 'Posts published' },
  smartLinkClicks: { id: 'app.sml.releases.overview.done.clicks', defaultMessage: 'SmartLink clicks' },
  clicksThisWeek: { id: 'app.sml.releases.overview.done.clicksWeek', defaultMessage: '+{count} this week' },
  presaves: { id: 'app.sml.releases.overview.done.presaves', defaultMessage: 'Pre-saves' },
  presavesEnded: { id: 'app.sml.releases.overview.done.presavesEnded', defaultMessage: 'Ended on release day' },
  failedPosts: {
    id: 'app.sml.releases.overview.done.failed',
    defaultMessage: '{count, plural, one {# failed} other {# failed}}',
  },
  viewPosts: { id: 'app.sml.releases.overview.done.viewPosts', defaultMessage: 'View posts' },
  plannedTitle: { id: 'app.sml.releases.overview.planned.title', defaultMessage: 'Planned' },
  plannedPushTitle: { id: 'app.sml.releases.overview.planned.pushTitle', defaultMessage: 'Planned: push' },
  plannedSubtitle: {
    id: 'app.sml.releases.overview.planned.subtitle',
    defaultMessage: 'The next posts of the rollout',
  },
  plannedPushSubtitle: {
    id: 'app.sml.releases.overview.planned.pushSubtitle',
    defaultMessage: 'Keep an older song in the feed',
  },
  plannedMeta: {
    id: 'app.sml.releases.overview.planned.meta',
    defaultMessage: '{planned} planned · {gaps, plural, one {# gap} other {# gaps}}',
  },
  // QA finding F23: plain words for a rollout day with no post.
  plannedMetaDays: {
    id: 'app.sml.releases.overview.planned.metaDays',
    defaultMessage: '{planned} planned · {gaps, plural, one {# day with no post} other {# days with no post}}',
  },
  plannedOnly: { id: 'app.sml.releases.overview.planned.only', defaultMessage: '{planned} planned' },
  nothingPlanned: { id: 'app.sml.releases.overview.planned.nothing', defaultMessage: 'nothing planned' },
  openPlan: { id: 'app.sml.releases.overview.planned.openPlan', defaultMessage: 'Open Plan' },
  openCalendar: { id: 'app.sml.releases.overview.planned.openCalendar', defaultMessage: 'Open Calendar' },
  noPostYet: { id: 'app.sml.releases.overview.planned.noPost', defaultMessage: 'no post yet' },
  addDraft: { id: 'app.sml.releases.overview.planned.addDraft', defaultMessage: 'Add a draft' },
  addDrafts: {
    id: 'app.sml.releases.overview.planned.addDrafts',
    defaultMessage: '{count, plural, one {Add # draft} other {Add # drafts}}',
  },
  planPush: { id: 'app.sml.releases.overview.planned.planPush', defaultMessage: 'Plan a push' },
  moreAfter: {
    id: 'app.sml.releases.overview.planned.moreAfter',
    defaultMessage: '{count, plural, one {# more day} other {# more days}} on the Plan tab.',
  },
  nothingAfter: {
    id: 'app.sml.releases.overview.planned.nothingAfter',
    defaultMessage: 'Nothing is planned after {date}.',
  },
  noDateTitle: {
    id: 'app.sml.releases.overview.noDate.title',
    defaultMessage: 'Set a release date to plan a rollout.',
  },
  noDateBody: {
    id: 'app.sml.releases.overview.noDate.body',
    defaultMessage:
      'The rollout puts posts on the days around the release: announce, teaser, countdown, out now and after. You can still post now in the Composer.',
  },
  setDate: { id: 'app.sml.releases.overview.noDate.setDate', defaultMessage: 'Set the date in Catalog' },
  postNow: { id: 'app.sml.releases.overview.noDate.postNow', defaultMessage: 'Post now in Composer' },
  draftsAdded: {
    id: 'app.sml.releases.overview.planned.added',
    defaultMessage: '{count, plural, one {# draft added.} other {# drafts added.}}',
  },
  assetsQueued: {
    id: 'app.sml.releases.overview.planned.queued',
    defaultMessage: '{count, plural, one {# asset is being made first.} other {# assets are being made first.}}',
  },
  madeTitle: { id: 'app.sml.releases.overview.made.title', defaultMessage: 'Content made' },
  madeSubtitle: { id: 'app.sml.releases.overview.made.subtitle', defaultMessage: 'Newest revision of each asset' },
  madeMeta: {
    id: 'app.sml.releases.overview.made.meta',
    defaultMessage: '{count, plural, one {# asset} other {# assets}}',
  },
  seeAll: { id: 'app.sml.releases.overview.made.seeAll', defaultMessage: 'See all' },
  justMade: { id: 'app.sml.releases.overview.made.justMade', defaultMessage: 'Just made' },
  justMadeSub: { id: 'app.sml.releases.overview.made.justMadeSub', defaultMessage: 'since you were here' },
  byType: { id: 'app.sml.releases.overview.made.byType', defaultMessage: 'By type' },
  newTag: { id: 'app.sml.releases.overview.made.new', defaultMessage: 'New' },
  revision: { id: 'app.sml.releases.overview.made.revision', defaultMessage: 'r{revision}' },
  zipNote: {
    id: 'app.sml.releases.overview.made.zipNote',
    defaultMessage: 'The zip holds the newest ready revision of every asset.',
  },
  nothingMade: { id: 'app.sml.releases.overview.made.nothing', defaultMessage: 'Nothing made for this song yet.' },
  canMakeTitle: { id: 'app.sml.releases.overview.canMake.title', defaultMessage: 'Can still make' },
  canMakeSubtitle: { id: 'app.sml.releases.overview.canMake.subtitle', defaultMessage: 'Not made for this song yet' },
  creditsLeft: {
    id: 'app.sml.releases.overview.canMake.creditsLeft',
    defaultMessage: '{count, plural, one {# video credit left this month} other {# video credits left this month}}',
  },
  creditsPlan: {
    id: 'app.sml.releases.overview.canMake.creditsPlan',
    defaultMessage: 'Your plan gives {limit} a month. They reset on {date}. Images cost nothing.',
  },
  creditsLeftTrial: {
    id: 'app.sml.releases.overview.canMake.creditsLeftTrial',
    defaultMessage: '{count, plural, one {# video credit left in the trial} other {# video credits left in the trial}}',
  },
  // QA 2026-10-28 (skp-16, lit-13): one clear line, what a credit buys, and no date before a full
  // stop (a short month such as "4 de nov." then gave "nov..").
  creditsTrial: {
    id: 'app.sml.releases.overview.canMake.creditsTrialCosts',
    defaultMessage:
      'Trial credits until {date}: a short video costs {short, plural, one {# credit} other {# credits}}, a full-song video {full, plural, one {# credit} other {# credits}}. Images are free.',
  },
  // QA 2026-10-28 skp-16: the credits count by calendar month, also in the trial (Settings says the
  // same). Say plainly that the trial gets the credits again because it runs into a new month.
  creditsTrialMore: {
    id: 'app.sml.releases.overview.canMake.creditsTrialNextMonth',
    defaultMessage:
      'Trial credits until {date}: a short video costs {short, plural, one {# credit} other {# credits}}, a full-song video {full, plural, one {# credit} other {# credits}}. Images are free. Credits count by calendar month. Your trial runs into a new month, so on {resetDate} you get {limit} more credits.',
  },
  creditsEach: {
    id: 'app.sml.releases.overview.canMake.creditsEach',
    defaultMessage: '{count, plural, one {# credit each} other {# credits each}}',
  },
  creditsReset: { id: 'app.sml.releases.overview.canMake.creditsReset', defaultMessage: 'They reset on {date}.' },
  free: { id: 'app.sml.releases.overview.canMake.free', defaultMessage: 'Free' },
  credits: {
    id: 'app.sml.releases.overview.canMake.credits',
    defaultMessage: '{count, plural, one {# credit} other {# credits}}',
  },
  needsCredits: {
    id: 'app.sml.releases.overview.canMake.needs',
    defaultMessage: '{count, plural, one {Needs # credit} other {Needs # credits}}',
  },
  proPlan: { id: 'app.sml.releases.overview.canMake.proPlan', defaultMessage: 'Pro plan' },
  everythingMade: {
    id: 'app.sml.releases.overview.canMake.everything',
    defaultMessage: 'Every asset type is made for this song. Make a new revision on the Assets tab.',
  },
  openStudio: { id: 'app.sml.releases.overview.canMake.openStudio', defaultMessage: 'Open Studio' },
  openStudioNote: {
    id: 'app.sml.releases.overview.canMake.openStudioNote',
    defaultMessage: 'The Studio opens with {title} picked.',
  },
  buyCredits: { id: 'app.sml.releases.overview.canMake.buy', defaultMessage: 'Buy credits' },
  askOwner: {
    id: 'app.sml.releases.overview.canMake.askOwner',
    defaultMessage: 'Ask the workspace owner for more credits. Only the owner buys credits.',
  },
  seePlans: { id: 'app.sml.releases.overview.canMake.seePlans', defaultMessage: 'See plans' },
  loadFailed: { id: 'app.sml.releases.overview.loadFailed', defaultMessage: 'We could not load this part.' },
  tryAgain: { id: 'app.sml.releases.overview.tryAgain', defaultMessage: 'Try again' },
});

/** The plural names of the asset groups (design b1 "By type", c1 group heads). */
export const revisionGroupMessages: Record<RevisionGroupId, MessageDescriptor> = defineMessages({
  stories: { id: 'app.sml.releases.group.stories', defaultMessage: 'Stories' },
  feedPosts: { id: 'app.sml.releases.group.feedPosts', defaultMessage: 'Feed posts' },
  lyricCards: { id: 'app.sml.releases.group.lyricCards', defaultMessage: 'Lyric cards' },
  coverSizes: { id: 'app.sml.releases.group.coverSizes', defaultMessage: 'Cover sizes' },
  profile: { id: 'app.sml.releases.group.profile', defaultMessage: 'Banners, link preview and YouTube thumbnail' },
  shortVideos: { id: 'app.sml.releases.group.shortVideos', defaultMessage: 'Short videos' },
  fullVideos: { id: 'app.sml.releases.group.fullVideos', defaultMessage: 'Full videos' },
  aiReels: { id: 'app.sml.releases.group.aiReels', defaultMessage: 'AI reels' },
});

/** "Send to your subscribers" (design presave-subscriptions d2). */
export const fanReleaseMessages = defineMessages({
  eyebrow: { id: 'app.sml.releases.overview.fanRelease.eyebrow', defaultMessage: 'Your fans' },
  sendTitle: {
    id: 'app.sml.releases.overview.fanRelease.sendTitle',
    defaultMessage: 'Send {title} to your {count, plural, one {# subscriber} other {# subscribers}}',
  },
  sendBody: {
    id: 'app.sml.releases.overview.fanRelease.sendBody',
    defaultMessage:
      '{title} goes into their libraries today, and they get an email that it is out. You send each song one time.',
  },
  sendButton: {
    id: 'app.sml.releases.overview.fanRelease.sendButton',
    defaultMessage: 'Send to {count, plural, one {# subscriber} other {# subscribers}}',
  },
  sending: { id: 'app.sml.releases.overview.fanRelease.sending', defaultMessage: 'Sending…' },
  sent: {
    id: 'app.sml.releases.overview.fanRelease.sent',
    defaultMessage: 'Sent to {count, plural, one {# subscriber} other {# subscribers}}',
  },
  seeFans: { id: 'app.sml.releases.overview.fanRelease.seeFans', defaultMessage: 'See every release on Fans' },
  tooOldTitle: {
    id: 'app.sml.releases.overview.fanRelease.tooOldTitle',
    defaultMessage: '{title} came out more than {days} days ago',
  },
  tooOldBody: {
    id: 'app.sml.releases.overview.fanRelease.tooOldBody',
    defaultMessage: 'Subscribers get new songs only. Your next release goes to them on release day.',
  },
  noServiceIdsTitle: {
    id: 'app.sml.releases.overview.fanRelease.noServiceIdsTitle',
    defaultMessage: 'We cannot find {title} on the services yet',
  },
  noServiceIdsBody: {
    id: 'app.sml.releases.overview.fanRelease.noServiceIdsBody',
    defaultMessage:
      'The song must be live on Spotify, Apple Music or another service before we can save it. Try again when it is live.',
  },
  checkAgain: { id: 'app.sml.releases.overview.fanRelease.checkAgain', defaultMessage: 'Check again' },
  planTitle: {
    id: 'app.sml.releases.overview.fanRelease.planTitle',
    defaultMessage: 'Put {title} in {count, plural, one {# library} other {# libraries}} today',
  },
  planBody: {
    id: 'app.sml.releases.overview.fanRelease.planBody',
    defaultMessage:
      'Your subscribers get {title} in their library and an email that it is out. Sending a song to your fans comes with {plan} and up.',
  },
  seePlans: { id: 'app.sml.releases.overview.fanRelease.seePlans', defaultMessage: 'See plans' },
});
